import { computed, onScopeDispose, ref, watch, type DeepReadonly } from 'vue'
import { defineStore } from 'pinia'
import { useWalletStore } from '@/modules/wallet/store/walletStore'
import { PaymentApi } from '../api/paymentApi'
import { usePortalFlows } from '../composables/usePortalFlows'
import { isPortalUuid } from '../services/portalSafety'
import type { ConversionStatusResponse } from '../types/payment'
import type { PortalFlowItem } from '../types/portal'

type Progress = DeepReadonly<ConversionStatusResponse | PortalFlowItem>
export function paymentComplete(item: Progress): boolean {
  if (!['prepaid', 'activated'].includes(item.status) || !item.flows?.length) return false
  const types = item.flows.map(flow => flow.type)
  if (new Set(types).size !== types.length) return false
  if ('types' in item && (types.length !== item.types.length || !item.types.every(type => types.includes(type)))) return false
  const legacy = 'attempt_id' in item && item.purchase_complete === undefined && item.flows.every(flow => flow.purchase_status === undefined)
  if (!legacy && (item.purchase_complete !== true || !item.flows.every(flow => flow.purchase_status === 'purchased'))) return false
  return item.status === 'activated'
    ? item.flows.every(flow => flow.status === 'activated')
    : item.flows.every(flow => ['prepaid', 'activated'].includes(flow.status)) && item.flows.some(flow => flow.status === 'prepaid')
}
export const paymentTerminal = (item: Progress) => paymentComplete(item) || item.status === 'failed' || item.status === 'exchanged' || (item.status === 'partially_purchased' && !!item.flows?.length && !item.flows.some(flow => flow.status === 'activating'))
const received = (item: Progress) => ['payment_confirmed', 'awaiting_liquidity', 'converting', 'credited', 'delayed', 'manual_review', 'exchanged', 'partially_purchased', 'prepaid', 'activating', 'activated'].includes(item.status)

/** One owner-scoped observer. No financial state is trusted from browser storage. */
export const usePaymentOperationsStore = defineStore('paymentOperations', () => {
  const owner = ref<number | null>(null)
  const site = ref('')
  const walletActive = ref<boolean | null>(null)
  const portal = usePortalFlows(() => owner.value, () => site.value, () => setSession(null, site.value), true, false)
  const conversions = ref<Record<string, ConversionStatusResponse | null>>({})
  const errors = ref<Record<string, boolean>>({})
  const unavailable = ref<string[]>([])
  const seen = ref<string[]>([])
  const dismissed = ref<string[]>([])
  const dismissing = ref<Record<string, boolean>>({})
  const dismissErrors = ref<Record<string, boolean>>({})
  const refreshed = new Set<string>()
  const busy = ref(false)
  let controller = new AbortController()
  let generation = 0
  let running = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let interval = 5000
  let cursor: string | undefined
  let nextDiscovery = 0
  let inFlight: Promise<void> | null = null
  const online = () => typeof navigator === 'undefined' || navigator.onLine !== false
  const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  const inWallet = () => walletActive.value ?? (typeof window !== 'undefined' && /^\/wallet(?:\/|$)/.test(window.location.pathname))
  function hasActivePortal(): boolean {
    if (!owner.value) return false
    if (portal.items.value.some(item => !paymentTerminal(item) || Boolean(portal.statusErrors.value[item.attempt_id]))) {
      return true
    }
    try {
      if (typeof sessionStorage !== 'undefined' && isPortalUuid(sessionStorage.getItem('gonkagame:portal:return'))) {
        return true
      }
    } catch { /* ignore */ }
    try {
      if (typeof localStorage !== 'undefined') {
        if (site.value && localStorage.getItem(`gonkagame:portal:flow:${site.value}:${owner.value}`)) return true
        if (localStorage.getItem(`gonkagame:portal:topup:${owner.value}`)) return true
      }
    } catch { /* ignore */ }
    return false
  }

  const all = computed(() => [
    ...portal.items.value.map(item => ({ key: `portal:${item.attempt_id}`, item })),
    ...Object.entries(conversions.value).flatMap(([id, item]) => item ? [{ key: `utp:${id}`, item }] : []),
  ])
  watch(all, values => {
    for (const { key, item } of values) {
      if ('notification_dismissed' in item && item.notification_dismissed && !dismissed.value.includes(key)) dismissed.value.push(key)
      if (received(item) && !paymentTerminal(item) && !seen.value.includes(key)) seen.value.push(key)
      if ((paymentComplete(item) || item.status === 'exchanged' || item.status === 'partially_purchased') && seen.value.includes(key)) {
        const resultKey = `${key}:${item.status}:${(item.flows ?? []).map(flow => `${flow.type}:${flow.purchase_status ?? ''}:${flow.status}`).sort().join('|')}`
        if (refreshed.has(resultKey)) continue
        refreshed.add(resultKey)
        // Dashboard failures must not invalidate the confirmed payment result.
        void Promise.resolve(useWalletStore().refreshDashboard()).catch(() => {})
      }
    }
  }, { flush: 'sync' })
  const notices = computed(() => all.value.filter(({ key, item }) =>
    received(item) && seen.value.includes(key) && !dismissed.value.includes(key),
  ).map(({ key, item }) => ({ key, item, complete: paymentComplete(item), terminal: paymentTerminal(item),
    dismissible: paymentTerminal(item) || (key.startsWith('utp:') && item.status === 'manual_review') })))

  function registerConversion(id: string) {
    if (!id) throw new Error('PAYMENT_ORDER_ID_REQUIRED')
    if (!(id in conversions.value)) conversions.value[id] = null
    if (!seen.value.includes(`utp:${id}`)) seen.value.push(`utp:${id}`)
  }
  function rememberPortal(item: PortalFlowItem) {
    if (!seen.value.includes(`portal:${item.attempt_id}`)) seen.value.push(`portal:${item.attempt_id}`)
    portal.remember(item)
  }
  async function dismiss(key: string) {
    if (!owner.value || dismissing.value[key] || !notices.value.find(notice => notice.key === key)?.dismissible) return
    if (!key.startsWith('utp:')) { dismissed.value.push(key); return }
    const expected = generation
    const signal = controller.signal
    const current = () => expected === generation && !signal.aborted
    dismissing.value[key] = true
    delete dismissErrors.value[key]
    try {
      await PaymentApi.dismissConversion(key.slice(4), signal)
      if (current()) dismissed.value.push(key)
    } catch (error) {
      if (!current()) return
      dismissErrors.value[key] = true
      const status = (error as { response?: { status?: number } }).response?.status
      if (status === 401 || status === 403) setSession(null, site.value)
    } finally { if (current()) delete dismissing.value[key] }
  }
  function setSession(id: number | null, scope: string) {
    if (id === owner.value && scope === site.value) return
    generation++; controller.abort(); controller = new AbortController(); portal.clear()
    clearTimeout(timer); inFlight = null; busy.value = false
    conversions.value = {}; errors.value = {}; unavailable.value = []; seen.value = []; dismissed.value = []
    dismissing.value = {}; dismissErrors.value = {}
    refreshed.clear()
    cursor = undefined; nextDiscovery = 0; interval = 5000
    site.value = scope; owner.value = id
    if (running) void tick()
  }
  function setWallet(active: boolean | null) {
    if (walletActive.value === active) return
    walletActive.value = active
    if (inWallet() && running && owner.value && hasActivePortal() && (!portal.loaded.value || portal.hasMore.value)) {
      void refresh()
    }
  }
  async function run() {
    const expected = generation
    const signal = controller.signal
    const current = () => expected === generation && !signal.aborted
    busy.value = true
    try {
      for (const [id, item] of Object.entries(conversions.value)) {
        if (!current()) return
        if ((item && paymentTerminal(item)) || unavailable.value.includes(id)) continue
        try {
          const result = await PaymentApi.getConversionStatus(id, signal)
          if (!current()) return
          conversions.value[id] = result; delete errors.value[`utp:${id}`]
        } catch (error) {
          if (!current()) return
          errors.value[`utp:${id}`] = true
          const status = (error as { response?: { status?: number } })?.response?.status
          if (status === 401 || status === 403) { setSession(null, site.value); return }
          if (status === 404) unavailable.value.push(id)
        }
      }
      for (const item of portal.items.value) {
        if (!current()) return
        if (!paymentTerminal(item) || portal.statusErrors.value[item.attempt_id]) await portal.refreshStatus(item.attempt_id)
      }
      if (!current()) return
      // Only fetch Portal flow list when in wallet with an active Portal transfer, and stop once loaded.
      if (inWallet() && hasActivePortal() && (!portal.loaded.value || portal.hasMore.value)) {
        await portal.discover()
      }
      if (!current()) return
      if (Date.now() >= nextDiscovery) {
        try {
          const page = await PaymentApi.getActiveConversions(cursor, signal)
          if (!current()) return
          for (const item of page.items) {
            if (!(item.order_id in conversions.value)) conversions.value[item.order_id] = item
          }
          cursor = page.next_cursor ?? undefined
          nextDiscovery = cursor ? 0 : Date.now() + 60000
        } catch (error) {
          if (!current()) return
          const status = (error as { response?: { status?: number } }).response?.status
          if (status === 401 || status === 403) setSession(null, site.value)
        }
      }
    } finally { if (current()) busy.value = false }
  }
  function refresh(): Promise<void> {
    if (inFlight) return inFlight
    if (!owner.value || !online() || !visible()) return Promise.resolve()
    const pending = run().finally(() => { if (inFlight === pending) inFlight = null })
    inFlight = pending
    return pending
  }
  async function tick() {
    clearTimeout(timer)
    const expected = generation
    await refresh()
    if (!running || expected !== generation) return
    clearTimeout(timer)
    timer = setTimeout(() => void tick(), interval)
    interval = Math.min(15000, interval + 1000)
  }
  function pause() {
    generation++; clearTimeout(timer); controller.abort(); controller = new AbortController()
    portal.cancel(); inFlight = null; busy.value = false
    dismissing.value = {}
  }
  function resume() {
    if (!online() || !visible()) { pause(); return }
    if (running) { interval = 5000; void tick() }
  }
  function start() {
    if (running) return
    running = true
    window.addEventListener('online', resume)
    window.addEventListener('offline', resume)
    document.addEventListener('visibilitychange', resume)
    void tick()
  }
  function stop() {
    running = false; pause()
    window.removeEventListener('online', resume)
    window.removeEventListener('offline', resume)
    document.removeEventListener('visibilitychange', resume)
  }
  onScopeDispose(stop)
  return { portal, conversions, errors, unavailable, notices, busy, dismissing, dismissErrors, registerConversion, rememberPortal, dismiss, setSession, setWallet, refresh, start, stop }
})
